import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import cookie from 'react-cookies';
import {
  extractRefreshExpiresIn,
  extractRefreshTokenId,
} from '../auth/normalizeLoginPayload';
import { REFRESH_COOKIE, TOKEN_COOKIE } from '../auth/cookies';
import {
  expireSessionToLogin,
  persistRefreshExpiry,
  registerRefreshHandler,
  startSessionWatch,
} from '../auth/session';

const TOKEN_COOKIE_NAME = TOKEN_COOKIE;

const getAuthToken = (): string | undefined => {
  try {
    return (cookie.load(TOKEN_COOKIE_NAME) as string) || undefined;
  } catch {
    return undefined;
  }
};

const getRefreshToken = (): string | undefined => {
  try {
    const raw = cookie.load(REFRESH_COOKIE) as unknown;
    if (typeof raw === 'string' && raw) return raw;
    if (raw && typeof raw === 'object') {
      const id = (raw as { id?: unknown }).id;
      if (typeof id === 'string' && id) return id;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

const isLoginOrRefreshRequest = (url?: string): boolean => {
  if (!url) return false;
  return /\/login(?:\?|$)/.test(url) || /\/refresh-token(?:\?|$)/.test(url);
};

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<boolean> | null = null;

const tryRefreshAccessToken = async (): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const authApiUrl = process.env.REACT_APP_AUTH_API_URL;
    const refreshId = getRefreshToken();
    if (!authApiUrl || !refreshId) return false;

    try {
      const { data } = await axios.post(
        `${authApiUrl}/refresh-token`,
        { refresh_token: refreshId },
        { timeout: 8_000 },
      );
      const access =
        typeof data?.access_token === 'string' && data.access_token
          ? data.access_token
          : '';
      if (!access) return false;

      cookie.save(TOKEN_COOKIE_NAME, access, { path: '/' });
      const nextRefresh = extractRefreshTokenId(data);
      if (nextRefresh) {
        cookie.save(REFRESH_COOKIE, nextRefresh, { path: '/' });
      }
      const refreshTtl = extractRefreshExpiresIn(data);
      if (refreshTtl) persistRefreshExpiry(refreshTtl);
      startSessionWatch();
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

registerRefreshHandler(tryRefreshAccessToken);

const normalizeResponseShape = (response: AxiosResponse) => ({
  body: response.data,
  status: response.status,
  statusText: response.statusText,
  headers: response.headers,
  ok: response.status >= 200 && response.status < 300,
});

const createClient = (baseURL?: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    response => response,
    error => {
      const original = error.config as RetryConfig | undefined;
      const status = error.response?.status;
      const isAuthEndpoint = isLoginOrRefreshRequest(original?.url);

      if (status !== 401 || isAuthEndpoint) {
        return Promise.reject(error);
      }

      const canRefresh =
        Boolean(original) && !original?._retry && Boolean(getRefreshToken());

      if (!canRefresh) {
        expireSessionToLogin();
        return Promise.reject(error);
      }

      original._retry = true;
      return tryRefreshAccessToken().then(refreshed => {
        if (!refreshed) {
          expireSessionToLogin();
          return Promise.reject(error);
        }

        const token = getAuthToken();
        if (token && original.headers) {
          original.headers['Authorization'] = `Bearer ${token}`;
        }
        return instance(original);
      });
    },
  );

  return instance;
};

// Default instance (no fixed baseURL — consumers pass full URL or set baseURL).
export const httpClient = createClient();

// The instance above fixes 'Content-Type: application/json' as a default header, which
// axios keeps even when the body is a FormData (multipart upload). Requests must omit
// Content-Type for FormData bodies so the browser can set 'multipart/form-data' with the
// correct boundary itself — use this helper for any multipart upload (e.g. image upload).
export const uploadFormData = <T = unknown>(
  url: string,
  formData: FormData,
  config?: AxiosRequestConfig,
) =>
  httpClient.post<T>(url, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': undefined },
  });

export { createClient, normalizeResponseShape };
export default httpClient;
